import React, { useState, useRef } from 'react';
import { Send, Lock, MessageSquare, Loader2 } from 'lucide-react';
import { cn } from '../../utils/helpers';

interface SupportMessageComposerProps {
  onSend: (content: string, type: 'APP' | 'INTERNAL_NOTE') => Promise<boolean>;
  disabled?: boolean;
}

type MessageMode = 'reply' | 'internal';

const SupportMessageComposer: React.FC<SupportMessageComposerProps> = ({
  onSend,
  disabled = false,
}) => {
  const [content, setContent] = useState('');
  const [mode, setMode] = useState<MessageMode>('reply');
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || sending || disabled) return;

    setSending(true);
    const success = await onSend(
      content.trim(),
      mode === 'reply' ? 'APP' : 'INTERNAL_NOTE'
    );

    if (success) {
      setContent('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    // Auto-resize
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
  };

  return (
    <div className="border-t border-border bg-card/50 p-4">
      {/* Mode toggle */}
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setMode('reply')}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
            mode === 'reply'
              ? 'bg-primary text-primary-foreground'
              : 'bg-secondary-700/50 text-muted-foreground hover:text-foreground'
          )}
        >
          <MessageSquare className="h-3 w-3" />
          Reply to Customer
        </button>
        <button
          type="button"
          onClick={() => setMode('internal')}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
            mode === 'internal'
              ? 'bg-warning text-warning-foreground'
              : 'bg-secondary-700/50 text-muted-foreground hover:text-foreground'
          )}
        >
          <Lock className="h-3 w-3" />
          Internal Note
        </button>
      </div>

      {/* Input form */}
      <form onSubmit={handleSubmit} className="relative">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || sending}
          placeholder={
            mode === 'reply'
              ? 'Type your reply to the customer...'
              : 'Add an internal note (not visible to customer)...'
          }
          rows={1}
          className={cn(
            'textarea pr-12 min-h-[44px] max-h-[200px]',
            mode === 'internal' && 'border-warning/30 focus:border-warning focus:ring-warning/50'
          )}
        />
        <button
          type="submit"
          disabled={!content.trim() || sending || disabled}
          className={cn(
            'absolute right-2 bottom-2 p-2 rounded-lg transition-all',
            content.trim() && !sending
              ? mode === 'reply'
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-warning text-warning-foreground hover:bg-warning/90'
              : 'bg-secondary-700/50 text-muted-foreground cursor-not-allowed'
          )}
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </form>

      {/* Helper text */}
      <p className="text-xs text-muted-foreground mt-2">
        Press <kbd className="px-1.5 py-0.5 bg-background rounded text-[10px]">Enter</kbd> to send,{' '}
        <kbd className="px-1.5 py-0.5 bg-background rounded text-[10px]">Shift+Enter</kbd> for new line
      </p>
    </div>
  );
};

export default SupportMessageComposer;
